import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Spin,
  message,
  Typography,
  Button,
} from 'antd';
import {
  DollarOutlined,
  UserOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { apiClient } from '../../config/apiClient';
import MainLayout from '../layout/layout';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const COLORS = ['#52c41a', '#8c8c8c', '#1890ff', '#faad14', '#eb2f96', '#13c2c2'];

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/dashboard');
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al cargar los datos del dashboard');
      }
    } catch (error) {
      console.error('Error al cargar dashboard:', error);
      message.error('Error al cargar los datos del dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatMoneda = (n) =>
    `$${Number(n).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  const formatMonth = (dateString) => dayjs(dateString).format('MMM YY');
  const formatDate = (dateString) => dayjs(dateString).format('DD/MM');

  // Datos para gráfico de alumnos (torta): activos vs inactivos
  const alumnosPieData = useMemo(() => {
    if (!dashboardData?.alumnos) return [];
    const { activos, inactivos } = dashboardData.alumnos;
    return [
      { name: 'Activos', value: activos, fill: '#52c41a' },
      { name: 'Inactivos', value: inactivos, fill: '#8c8c8c' },
    ].filter((d) => d.value > 0);
  }, [dashboardData?.alumnos]);

  // Datos para gráfico de ingresos por mes (barras)
  const ingresosPorMesData = useMemo(() => {
    if (!dashboardData?.graficos?.ingresosPorMes) return [];
    return [...dashboardData.graficos.ingresosPorMes]
      .reverse()
      .map((item) => ({
        mes: formatMonth(item.mes),
        total: item.total,
        cantidadPagos: item.cantidadPagos,
      }));
  }, [dashboardData?.graficos?.ingresosPorMes]);

  // Datos para gráfico de ingresos por día (líneas)
  const ingresosPorDiaData = useMemo(() => {
    if (!dashboardData?.graficos?.ingresosPorDia) return [];
    return [...dashboardData.graficos.ingresosPorDia]
      .reverse()
      .slice(-15)
      .map((item) => ({
        dia: formatDate(item.dia),
        total: item.total,
        cantidadPagos: item.cantidadPagos,
      }));
  }, [dashboardData?.graficos?.ingresosPorDia]);

  if (loading && !dashboardData) {
    return (
      <MainLayout>
        <div
          style={{
            padding: 50,
            textAlign: 'center',
            minHeight: 'calc(100vh - 64px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Spin size="large" tip="Cargando dashboard..." />
        </div>
      </MainLayout>
    );
  }

  if (!dashboardData) {
    return (
      <MainLayout>
        <div style={{ padding: 20 }}>
          <Card>
            <p>No se pudieron cargar los datos del dashboard</p>
          </Card>
        </div>
      </MainLayout>
    );
  }

  const { ingresos, pagos, alumnos } = dashboardData;

  return (
    <MainLayout>
      <div style={{ padding: isMobile ? 16 : 24 }}>
        <div
          style={{
            marginBottom: 24,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 16,
          }}
        >
          <Title level={3} style={{ margin: 0, color: '#ffffff' }}>
            Dashboard - Gestión de Academia
          </Title>
          <Button icon={<ReloadOutlined />} onClick={loadDashboardData} loading={loading}>
            Actualizar
          </Button>
        </div>

        {/* Resumen: Ingresos y pagos */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={8}>
            <Card style={{ background: '#2d2d2d', borderColor: '#404040' }}>
              <Statistic
                title="Ingresos totales"
                value={ingresos.total}
                prefix={<DollarOutlined style={{ color: '#52c41a' }} />}
                formatter={(v) => formatMoneda(v)}
                valueStyle={{ color: '#ffffff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card style={{ background: '#2d2d2d', borderColor: '#404040' }}>
              <Statistic
                title="Ingresos del mes"
                value={ingresos.mesActual}
                prefix={<DollarOutlined style={{ color: '#1890ff' }} />}
                formatter={(v) => formatMoneda(v)}
                valueStyle={{ color: '#ffffff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card style={{ background: '#2d2d2d', borderColor: '#404040' }}>
              <Statistic
                title="Pagos pendientes"
                value={pagos.pendientes.cantidad}
                prefix={<ClockCircleOutlined style={{ color: '#faad14' }} />}
                valueStyle={{ color: '#ffffff' }}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {formatMoneda(pagos.pendientes.montoTotal)} total
              </Text>
            </Card>
          </Col>
        </Row>

        {/* Resumen: Alumnos */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={8}>
            <Card style={{ background: '#2d2d2d', borderColor: '#404040' }}>
              <Statistic
                title="Alumnos activos"
                value={alumnos.activos}
                prefix={<UserOutlined style={{ color: '#52c41a' }} />}
                valueStyle={{ color: '#ffffff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card style={{ background: '#2d2d2d', borderColor: '#404040' }}>
              <Statistic
                title="Alumnos inactivos"
                value={alumnos.inactivos}
                prefix={<UserOutlined style={{ color: '#8c8c8c' }} />}
                valueStyle={{ color: '#ffffff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card style={{ background: '#2d2d2d', borderColor: '#404040' }}>
              <Statistic
                title="Total alumnos"
                value={alumnos.activos + alumnos.inactivos}
                prefix={<UserOutlined style={{ color: '#1890ff' }} />}
                valueStyle={{ color: '#ffffff' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Gráficos */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} lg={12}>
            <Card
              title="Alumnos activos vs inactivos"
              style={{ background: '#2d2d2d', borderColor: '#404040', height: '100%' }}
            >
              {alumnosPieData.length === 0 ? (
                <div
                  style={{
                    height: 280,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Text type="secondary">Sin datos de alumnos</Text>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={alumnosPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={{ stroke: '#8c8c8c' }}
                    >
                      {alumnosPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill || COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ background: '#1f1f1f', border: '1px solid #404040', borderRadius: 8 }}
                      labelStyle={{ color: '#e8e8e8' }}
                      formatter={(value) => [`${value} alumnos`, 'Cantidad']}
                      labelFormatter={(label) => label}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card
              title="Ingresos por mes"
              style={{ background: '#2d2d2d', borderColor: '#404040', height: '100%' }}
            >
              {ingresosPorMesData.length === 0 ? (
                <div
                  style={{
                    height: 280,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Text type="secondary">Sin ingresos en el período</Text>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={ingresosPorMesData} margin={{ top: 16, right: 24, left: 24, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#404040" />
                    <XAxis dataKey="mes" stroke="#8c8c8c" tick={{ fill: '#b0b0b0' }} />
                    <YAxis stroke="#8c8c8c" tick={{ fill: '#b0b0b0' }} tickFormatter={(v) => `$${v}`} />
                    <Tooltip
                      contentStyle={{ background: '#1f1f1f', border: '1px solid #404040', borderRadius: 8 }}
                      labelStyle={{ color: '#e8e8e8' }}
                      formatter={(value) => [formatMoneda(value), 'Ingresos']}
                      labelFormatter={(label) => label}
                    />
                    <Bar dataKey="total" name="Ingresos" fill="#52c41a" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>
          </Col>
        </Row>

        {/* Ingresos por día (últimos 15 días) */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24}>
            <Card
              title="Ingresos por día (últimos 15 días)"
              style={{ background: '#2d2d2d', borderColor: '#404040' }}
            >
              {ingresosPorDiaData.length === 0 ? (
                <div
                  style={{
                    height: 300,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Text type="secondary">Sin ingresos en el período</Text>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={ingresosPorDiaData} margin={{ top: 16, right: 24, left: 24, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#404040" />
                    <XAxis dataKey="dia" stroke="#8c8c8c" tick={{ fill: '#b0b0b0' }} />
                    <YAxis stroke="#8c8c8c" tick={{ fill: '#b0b0b0' }} tickFormatter={(v) => `$${v}`} />
                    <Tooltip
                      contentStyle={{ background: '#1f1f1f', border: '1px solid #404040', borderRadius: 8 }}
                      labelStyle={{ color: '#e8e8e8' }}
                      formatter={(value) => [formatMoneda(value), 'Ingresos']}
                      labelFormatter={(label) => label}
                    />
                    <Legend wrapperStyle={{ color: '#b0b0b0' }} />
                    <Line
                      type="monotone"
                      dataKey="total"
                      name="Ingresos"
                      stroke="#1890ff"
                      strokeWidth={2}
                      dot={{ fill: '#1890ff' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Card>
          </Col>
        </Row>

        {/* Tabla detalle ingresos por mes */}
        {ingresosPorMesData.length > 0 && (
          <Card
            title="Detalle de ingresos por mes"
            style={{ background: '#2d2d2d', borderColor: '#404040' }}
          >
            <Table
              columns={[
                { title: 'Mes', dataIndex: 'mes', key: 'mes' },
                {
                  title: 'Total',
                  dataIndex: 'total',
                  key: 'total',
                  align: 'right',
                  render: (v) => formatMoneda(v),
                },
                {
                  title: 'Cantidad de pagos',
                  dataIndex: 'cantidadPagos',
                  key: 'cantidadPagos',
                  align: 'center',
                },
              ]}
              dataSource={ingresosPorMesData.map((item, i) => ({ ...item, key: i }))}
              pagination={false}
              size="small"
              scroll={isMobile ? { x: 280 } : undefined}
            />
          </Card>
        )}
      </div>
    </MainLayout>
  );
};

export default Dashboard;
